import { ConditionOperatorEnumType, CountOperator } from '@reach/shared-types/conditions';
import React, { useState } from 'react';
import type { AvailableFieldsResponse, RelatedSchema } from '../types';
import styles from './FieldsAndOperatorsPanel.module.css';

const AVAILABLE_OPERATORS = Object.values(ConditionOperatorEnumType);
const COUNT_OPERATORS = Object.values(CountOperator);

interface FieldsAndOperatorsPanelProps {
  availableFields: AvailableFieldsResponse;
  isLoadingFields: boolean;
  fieldsError?: Error | null;
  /** When true, show legacy field names (fields_to_expose.name) for contents format. */
  useLegacyFieldNames?: boolean;
}

interface TooltipPosition {
  top: number;
  left: number;
}

function TagList({
  items,
  variant = 'default',
}: {
  items: string[];
  variant?: 'default' | 'operators';
}): React.ReactElement {
  const [copiedItem, setCopiedItem] = useState<string | null>(null);
  const [tooltipPos, setTooltipPos] = useState<TooltipPosition | null>(null);

  const handleCopy = async (e: React.MouseEvent<HTMLButtonElement>, text: string) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPos({
      top: rect.bottom + 4,
      left: rect.left + rect.width / 2,
    });
    await navigator.clipboard.writeText(text);
    setCopiedItem(text);
    setTimeout(() => {
      setCopiedItem(null);
      setTooltipPos(null);
    }, 1500);
  };

  const className = variant === 'operators' ? `${styles.tagList} ${styles.operators}` : styles.tagList;

  return (
    <ul className={className}>
      {items.map(item => (
        <li key={item}>
          <button type="button" onClick={e => handleCopy(e, item)}>
            {item}
          </button>
        </li>
      ))}
      {copiedItem && tooltipPos && (
        <span
          className={styles.copiedTooltip}
          style={{
            top: tooltipPos.top,
            left: tooltipPos.left,
            transform: 'translateX(-50%)',
          }}>
          Copied!
        </span>
      )}
    </ul>
  );
}

function FieldsSection({
  fields,
  isLoading,
  error,
}: {
  fields: string[];
  isLoading: boolean;
  error?: Error | null;
}): React.ReactElement {
  if (isLoading) {
    return <p>Loading fields...</p>;
  }

  if (error) {
    return <p className={styles.error}>Failed to load fields.</p>;
  }

  if (fields.length === 0) {
    return <p>No custom fields configured.</p>;
  }

  return <TagList items={fields} />;
}

const CONDITION_GROUPS_EXAMPLE = `{
  "conditionGroups": [
    {
      "logic": "and",
      "conditions": [
        { "field": "status", "operator": "equals", "value": ["active"] },
        {
          "type": "related_resource",
          "relatedResource": {
            "schemaId": "...",
            "foreignKeyPath": "customerId"
          },
          "countOperator": "gte",
          "countValue": 1,
          "conditions": [
            { "field": "type", "operator": "contains", "value": ["personal_"] }
          ]
        },
        {
          "type": "related_resource",
          "relatedResource": {
            "schemaId": "...",
            "foreignKeyPath": "customerId"
          },
          "countOperator": "eq",
          "countValue": 0,
          "conditions": [
            { "field": "status", "operator": "equals", "value": ["cancelled"] }
          ]
        }
      ]
    }
  ]
}`;

function ConditionGroupsExample(): React.ReactElement {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(CONDITION_GROUPS_EXAMPLE);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className={styles.syntaxBlock}>
      <p className={styles.syntaxLabel}>Condition groups example (copy):</p>
      <pre className={styles.syntaxPre}>{CONDITION_GROUPS_EXAMPLE}</pre>
      <button type="button" onClick={handleCopy} className={styles.copyBlockButton}>
        {copied ? 'Copied!' : 'Copy example'}
      </button>
    </div>
  );
}

function RelatedSchemaSection({
  schema,
  useLegacyFieldNames,
}: {
  schema: RelatedSchema;
  useLegacyFieldNames?: boolean;
}): React.ReactElement {
  const fields = useLegacyFieldNames ? (schema.fieldsLegacy ?? schema.fields) : schema.fields;
  const hasFields = fields.length > 0;
  return (
    <details className={styles.relatedSchema}>
      <summary>
        <span className={styles.relatedSchemaName}>{schema.name}</span>
        <span className={styles.relatedSchemaId}>{schema.id}</span>
        {!hasFields && <span className={styles.noFieldsBadge}>no fields exposed</span>}
      </summary>
      <div className={styles.relatedSchemaBody}>
        <p className={styles.foreignKey}>
          Foreign Key: <code>{schema.foreignKeyPath}</code>
        </p>
        <p className={styles.countOperatorsLabel}>Count operators:</p>
        <TagList items={COUNT_OPERATORS} variant="operators" />
        <p className={styles.fieldsLabel}>Fields:</p>
        {hasFields ? (
          <TagList items={fields} />
        ) : (
          <p className={styles.noFieldsMessage}>
            No fields exposed for this schema. Add fields to <code>fields_to_expose</code> in the schema
            definition to use them in segment conditions.
          </p>
        )}
      </div>
    </details>
  );
}

export const FieldsAndOperatorsPanel: React.FC<FieldsAndOperatorsPanelProps> = ({
  availableFields,
  isLoadingFields,
  fieldsError,
  useLegacyFieldNames,
}) => {
  const contactFields = useLegacyFieldNames
    ? (availableFields?.contactFieldsLegacy ?? availableFields?.contactFields ?? [])
    : (availableFields?.contactFields ?? []);
  const relatedSchemas = availableFields?.relatedSchemas ?? [];

  return (
    <details className={styles.panel}>
      <summary>View available attributes and operators</summary>
      <section>
        <p className={styles.hint}>Click any item to copy</p>

        <details className={styles.sectionGroup} open>
          <summary className={styles.sectionSummary}>Contact fields</summary>
          <div className={styles.sectionContent}>
            <FieldsSection fields={contactFields} isLoading={isLoadingFields} error={fieldsError} />
          </div>
        </details>

        {relatedSchemas.length > 0 && (
          <details className={styles.sectionGroup}>
            <summary className={styles.sectionSummary}>Related resources</summary>
            <div className={styles.sectionContent}>
              <p className={styles.relatedHint}>
                Use these in condition groups with type &quot;related_resource&quot;
              </p>
              {relatedSchemas.map(schema => (
                <RelatedSchemaSection
                  key={schema.id}
                  schema={schema}
                  useLegacyFieldNames={useLegacyFieldNames}
                />
              ))}
            </div>
          </details>
        )}

        <div>
          <h4>Operators:</h4>
          <TagList items={AVAILABLE_OPERATORS} variant="operators" />
        </div>

        <details className={styles.sectionGroup}>
          <summary className={styles.sectionSummary}>Condition groups syntax</summary>
          <div className={styles.sectionContent}>
            <p className={styles.relatedHint}>
              Use condition groups for AND/OR logic and related-resource conditions.
            </p>
            <ConditionGroupsExample />
          </div>
        </details>
      </section>
    </details>
  );
};
